import { useEffect, useState } from "react";
import { AlertCircle, RotateCcw, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * PauseExtensionNudge — gentle in-app banner shown to paused members when
 * their 30-day pause window is almost up (≤7 days left). Offers two paths:
 *   • "Resume now" — call /memberships/reactivate
 *   • "Extend another 30 days" — call /memberships/pause again (server should
 *     just refresh the pause window when called on an already-paused user).
 *
 * Sits at the top of /account pages. Session-dismissible.
 */

const DISMISS_KEY = "ff2d_pause_nudge_dismissed_v1";
const NUDGE_WINDOW_DAYS = 7;

export default function PauseExtensionNudge() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/memberships/status")
      .then(r => setStatus(r.data))
      .catch(() => {});
  }, [user]);

  if (dismissed) return null;
  if (!status?.paused) return null;

  // Compute days remaining on the pause window
  const resumeAt = status.paused_until || status.resume_at;
  if (!resumeAt) return null;
  const msLeft = new Date(resumeAt).getTime() - Date.now();
  const daysLeft = Math.ceil(msLeft / 86400000);
  if (daysLeft > NUDGE_WINDOW_DAYS) return null;
  if (daysLeft < 0) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const call = async (endpoint, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(endpoint);
      await refresh();
      dismiss();
    } catch { /* noop */ }
    setBusy(false);
    if (msg) {
      try { alert(msg); } catch { /* noop */ }
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto px-6 pt-6"
      data-testid="pause-extension-nudge"
    >
      <div className="bg-[var(--ff-accent-kraft)]/25 border border-[var(--ff-accent-kraft)] rounded-2xl p-5 flex flex-wrap items-center gap-4">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[var(--ff-primary)] text-white flex-shrink-0">
          <Clock size={18}/>
        </span>
        <div className="flex-1 min-w-[240px]">
          <p className="font-display text-lg text-[var(--ff-primary)] leading-tight" data-testid="pause-nudge-heading">
            Your pause ends in {daysLeft === 0 ? "less than a day" : `${daysLeft} day${daysLeft === 1 ? "" : "s"}`}.
          </p>
          <p className="text-sm text-[var(--ff-text-secondary)] mt-1">
            Ready to come back? Or need more time — we&apos;ve got you.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => call("/memberships/reactivate")}
            disabled={busy}
            className="btn-primary inline-flex items-center gap-1.5 text-sm px-4 py-2"
            data-testid="pause-nudge-resume"
          >
            <RotateCcw size={14}/> Resume now
          </button>
          <button
            type="button"
            onClick={() => call("/memberships/pause")}
            disabled={busy}
            className="btn-secondary inline-flex items-center gap-1.5 text-sm px-4 py-2"
            data-testid="pause-nudge-extend"
          >
            <AlertCircle size={14}/> Extend 30 more days
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="text-sm text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] transition-colors px-2"
            data-testid="pause-nudge-dismiss"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
